import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DashboardGridItem } from './dashboardEditSlice';

export interface DashboardLayoutPreset {
  id: string;
  name: string;
  description?: string;
  layout: DashboardGridItem[];
  createdAt: string;
  appliedCount: number;
}

export interface LayoutPresetPreview {
  layout: DashboardGridItem[];
  isLoading: boolean;
}

export interface LayoutPresetsState {
  presets: DashboardLayoutPreset[];
  currentPresetId: string | null;
  presetPreview: LayoutPresetPreview;
  error: string | null;
}

const initialState: LayoutPresetsState = {
  presets: [],
  currentPresetId: null,
  presetPreview: {
    layout: [],
    isLoading: false,
  },
  error: null,
};

const layoutPresetsSlice = createSlice({
  name: 'layoutPresets',
  initialState,
  reducers: {
    setPresets(state, action: PayloadAction<DashboardLayoutPreset[]>) {
      state.presets = action.payload;
      state.error = null;
    },
    setCurrentPresetId(state, action: PayloadAction<string | null>) {
      state.currentPresetId = action.payload;
    },
    setPresetPreview(state, action: PayloadAction<LayoutPresetPreview>) {
      state.presetPreview = action.payload;
    },
    clearPresetPreview(state) {
      state.presetPreview = { layout: [], isLoading: false };
    },
    setLayoutPresetsError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    upsertPreset(state, action: PayloadAction<DashboardLayoutPreset>) {
      const index = state.presets.findIndex((preset) => preset.id === action.payload.id);
      if (index >= 0) {
        state.presets[index] = action.payload;
      } else {
        state.presets.unshift(action.payload);
      }
      state.error = null;
    },
    removePreset(state, action: PayloadAction<string>) {
      state.presets = state.presets.filter((preset) => preset.id !== action.payload);
      if (state.currentPresetId === action.payload) {
        state.currentPresetId = null;
      }
    },
  },
});

export const {
  setPresets,
  setCurrentPresetId,
  setPresetPreview,
  clearPresetPreview,
  setLayoutPresetsError,
  upsertPreset,
  removePreset,
} = layoutPresetsSlice.actions;

export default layoutPresetsSlice.reducer;
